/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { useCommonPreferences } from "../context/common-preferences.js";
import { useNotificationContext } from "../context/notification.js";
import { useTranslationContext } from "../context/translation.js";
import { Attention } from "./Attention.js";

export function ToastBanner({
  compact = false,
  messageType,
}: {
  compact?: boolean;
  messageType?: "error" | "info";
}): VNode {
  const { i18n } = useTranslationContext();
  const { notification: notifications } = useNotificationContext();
  const [{ showDebugInfo }] = useCommonPreferences();
  const notification = messageType
    ? notifications.find((item) => item.message.type === messageType)
    : notifications[0];
  const [expandedNotification, setExpandedNotification] =
    useState<typeof notification>();
  const moreInfo = expandedNotification === notification;
  if (!notification) return <Fragment />;

  if (compact) {
    const isError = notification.message.type === "error";
    const errorMessage =
      notification.message.type === "error" ? notification.message : undefined;
    const descriptions = errorMessage?.description ?? [];
    const visibleDescriptions = moreInfo
      ? descriptions
      : descriptions.slice(0, 1);
    const debug =
      errorMessage && showDebugInfo
        ? JSON.stringify(
            errorMessage.debug,
            (key, value) => (key.startsWith("__") ? "..." : value),
            2,
          )
        : undefined;
    const copyText = [notification.message.title, ...descriptions, debug]
      .filter((entry): entry is string => !!entry)
      .join("\n\n");

    return (
      <div
        class={`rounded-md border px-3 py-2 text-sm ${
          isError
            ? "border-red-200 bg-red-50 text-red-800"
            : "border-green-200 bg-green-50 text-green-800"
        }`}
        role={isError ? "alert" : "status"}
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <div class="font-semibold">{notification.message.title}</div>
            {visibleDescriptions.map((description, index) => (
              <p key={index} class="mt-1">
                {description}
              </p>
            ))}
            {moreInfo && debug ? (
              <pre class="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-white/70 p-2 text-xs text-onBackground">
                {debug}
              </pre>
            ) : undefined}
            {isError ? (
              <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
                {descriptions.length > 1 || debug ? (
                  <button
                    type="button"
                    class="underline hover:no-underline"
                    aria-expanded={moreInfo}
                    onClick={() =>
                      setExpandedNotification((current) =>
                        current === notification ? undefined : notification,
                      )
                    }
                  >
                    {moreInfo ? (
                      <i18n.Translate>Hide details</i18n.Translate>
                    ) : (
                      <i18n.Translate>Show details</i18n.Translate>
                    )}
                  </button>
                ) : undefined}
                {copyText ? (
                  <button
                    type="button"
                    class="underline hover:no-underline"
                    onClick={() => {
                      void navigator.clipboard
                        ?.writeText(copyText)
                        .catch((error) =>
                          console.error("Could not copy notification", error),
                        );
                    }}
                  >
                    <i18n.Translate>Copy details</i18n.Translate>
                  </button>
                ) : undefined}
              </div>
            ) : undefined}
          </div>
          <button
            type="button"
            class="shrink-0 rounded px-1 text-lg leading-none hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            aria-label={i18n.str`Dismiss notification`}
            onClick={notification.acknowledge}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    );
  }

  if (notification.message.type === "info") {
    return (
      <Attention
        type="success"
        title={notification.message.title}
        onClose={notification.acknowledge}
      />
    );
  }

  const descriptions = notification.message.description ?? [];
  const debug = showDebugInfo
    ? JSON.stringify(
        notification.message.debug,
        (key, value) => (key.startsWith("__") ? "..." : value),
        2,
      )
    : undefined;
  const copyText = [notification.message.title, ...descriptions, debug]
    .filter((entry): entry is string => !!entry)
    .join("\n\n");
  return (
    <Attention
      type="danger"
      title={notification.message.title}
      copy
      copyText={copyText}
      onClose={() => {
        notification.acknowledge();
        setExpandedNotification(undefined);
      }}
    >
      {(moreInfo ? descriptions : descriptions.slice(0, 1)).map(
        (description, index) => (
          <div key={index} class="mt-2 text-sm text-red-700">
            {description}
          </div>
        ),
      )}
      {!moreInfo && descriptions.length > 1 ? (
        <button
          type="button"
          onClick={() => setExpandedNotification(notification)}
          class="text-grey"
        >
          <i18n.Translate>Show more info</i18n.Translate>
        </button>
      ) : undefined}
      {debug ? (
        <pre class="whitespace-break-spaces text-black">{debug}</pre>
      ) : undefined}
    </Attention>
  );
}
